import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import pilotMovies from "../data/pilot_support20_onboarding.json";
import pilotManifest from "../data/pilot_support20_onboarding.manifest.json";
import servingDeployment from "../data/serving_deployment.json";
import {
  resolveMovieLensRecommendation,
  filterRecommendationRecords,
} from "../utils/tmdbMetadata.mjs";
import { deduplicateSelectableCatalog, appendCatalogPage, selectableCatalogMovieIds } from "../utils/selectableCatalog.mjs";
import { publicAsset } from "../utils/publicAsset.mjs";
import { apiErrorMessage } from "../utils/apiError.mjs";
import { PILOT_API_URL } from "../utils/apiConfig";
import { useStudySession } from "../study/useStudySession";
import { useRecommendationSession, useSessionValue } from "../study/useRecommendationSession";
import { moviePosterMetadata } from "../utils/posterMetadata.mjs";
import MoviePoster from "./MoviePoster";
import MoviePosterPreview from "./MoviePosterPreview";
import RecommendationGrid from "./RecommendationGrid";
import SelectedMovies from "./SelectedMovies";
import { usePageRefresh } from "../study/pageRefresh";
import {
  combinedGenrePreferences,
  genreNamesWithFrequencies,
  MAX_GERS_GENRE_WEIGHT,
  recommendationInputSnapshot,
  responseMatchesCurrent,
} from "../study/studyProtocol.mjs";


const MIN_RECOMMENDATION_YEAR = servingDeployment.candidate_filter.value;
const GERS_ALPHA = 0.5;
// IMAX is a MovieLens category without a TMDB genre ID.
const IMAX_GENRE_ID = "imax";
const FIXED_MOVIELENS_CATALOG = deduplicateSelectableCatalog(pilotMovies);
const CATALOG_IDS = selectableCatalogMovieIds(FIXED_MOVIELENS_CATALOG);

function normalizeGenre(g) {
  return g
    .replace(/-/g, " ")
    .replace(/science fiction/i, "Science Fiction")
    .replace(/sci fi/i, "Sci-Fi")
    .replace(/sci-fi/i, "Sci-Fi")
    .trim();
}

/* ---------------------------------------------------------
    TMDB GENRE MAP
----------------------------------------------------------*/
const GENRE_MAP = {
  "Action": 28,
  "Adventure": 12,
  "Animation": 16,
  "Comedy": 35,
  "Crime": 80,
  "Documentary": 99,
  "Drama": 18,
  "Family": 10751,
  "Fantasy": 14,
  "History": 36,
  "Horror": 27,
  "IMAX": IMAX_GENRE_ID,
  "Music": 10402,
  "Mystery": 9648,
  "Romance": 10749,
  "Science Fiction": 878,
  "Sci-Fi": 878,
  "Sci Fi": 878,
  "SciFi": 878,
  "Thriller": 53,
  "War": 10752,
  "Western": 37,
  "TV Movie": 10770
};

/* -----------------------------------------------
   TMDB POSTER FETCHER
------------------------------------------------ */
async function fetchPoster(movieId, title, tmdbId = null, imdbId = null) {
  return moviePosterMetadata(movieId, title, tmdbId, imdbId);
}

/* -----------------------------------------------
   MAIN GERS APP
------------------------------------------------ */
export default function GersApp({ goBack }) {
  const study = useStudySession("GERS");
  const session = useRecommendationSession("GERS", study, CATALOG_IDS);
  const { recommendations, setRecommendations, recordCatalog } = session;
  const recommendationsRef = useRef(recommendations);
  recommendationsRef.current = recommendations;
  const [movies, setMovies] = useState([]);
  const [loadingMovies, setLoadingMovies] = useState(true);

  const [selected, setSelected] = useSessionValue(`${session.prefix}:selected`, []);
  const selectedMovieIds = new Set(selectableCatalogMovieIds(selected));
  const catalogMovies = movies.filter((movie) => !selectedMovieIds.has(Number(movie.movieId)));
  const [manualGenreWeights, setManualGenreWeights] = useSessionValue(`${session.prefix}:manualGenreWeights`, {});
  const [removedMovieGenres, setRemovedMovieGenres] = useSessionValue(`${session.prefix}:removedMovieGenres`, []);

  const context = "";
  const [topK, setTopK] = useSessionValue(`${session.prefix}:topK`, 12);
  const [loading, setLoading] = useState(false);
  const [recommendationError, setRecommendationError] = useState("");
  const representationRevisionRef = useRef(0);
  const recommendationRequestId = useRef(0);
  const activeRecommendationRequest = useRef(null);
  const currentRecommendationSignature = useRef("dirty-initial");

  const bumpRepresentationRevision = () => {
    representationRevisionRef.current += 1;
  };

  const invalidateRecommendations = () => {
    session.invalidate();
    recommendationRequestId.current += 1;
    activeRecommendationRequest.current = null;
    currentRecommendationSignature.current = `dirty-${recommendationRequestId.current}`;
    session.setNotice("");
    setRecommendations([]);
    setRecommendationError("");
    setLoading(false);
  };

  /* FIXED GENRE SET */
  const genreList = [
  { id: 28, name: "Action" },
  { id: 12, name: "Adventure" },
  { id: 16, name: "Animation" },
  { id: 35, name: "Comedy" },
  { id: 80, name: "Crime" },
  { id: 99, name: "Documentary" },
  { id: 18, name: "Drama" },
  { id: 10751, name: "Family" },
  { id: 14, name: "Fantasy" },
  { id: 36, name: "History" },
  { id: 27, name: "Horror" },
  { id: IMAX_GENRE_ID, name: "IMAX" },
  { id: 10402, name: "Music" },
  { id: 9648, name: "Mystery" },
  { id: 10749, name: "Romance" },
  { id: 878, name: "Science Fiction" },
  { id: 53, name: "Thriller" },
  { id: 10752, name: "War" },
  { id: 37, name: "Western" },
  { id: 10770, name: "TV Movie" }
];

  const genrePreferences = combinedGenrePreferences({
    selectedMovies: selected,
    removedMovieGenres,
    manualGenreWeights,
    genreList,
  });
  const totalGenreWeight = genrePreferences.reduce(
    (total, genre) => total + genre.weight, 0
  );
  const genreWeightLimitReached = totalGenreWeight >= MAX_GERS_GENRE_WEIGHT;

  /* -----------------------------------------------
     LOAD MOVIES
  ------------------------------------------------ */

/* -----------------------------------------------
   LOAD MOVIES — FIXED LIST WITH GENRE IDS
------------------------------------------------ */
useEffect(() => {
  let cancelled = false;

  async function loadFixedMovieLensCatalog() {
    setLoadingMovies(true);
    const mapped = await Promise.all(
      FIXED_MOVIELENS_CATALOG.map(async (movie) => {
        const metadata = await fetchPoster(
          movie.movieId,
          movie.title,
          movie.tmdbId,
          movie.imdbId
        );
        return {
          id: movie.movieId,
          movieId: movie.movieId,
          title: movie.title,
          genres: movie.genres,
          poster: metadata?.poster_url || null,
          poster_urls: metadata?.poster_urls || [],
          overview: metadata?.overview || "No summary available.",
          rating: metadata?.rating ?? null,
          year:
            metadata?.release_year ||
            movie.title.match(/\((\d{4})\)\s*$/)?.[1] ||
            "",
          genre_ids: movie.genres
            .map((genre) => GENRE_MAP[normalizeGenre(genre)] || null)
            .filter(Boolean),
        };
      })
    );

    if (!cancelled) {
      const visible = appendCatalogPage([], mapped, recommendationsRef.current);
      recordCatalog(visible);
      setMovies(visible);
      setLoadingMovies(false);
    }
  }

  loadFixedMovieLensCatalog();
  return () => {
    cancelled = true;
  };
}, [recordCatalog]);


  /* -----------------------------------------------
     SELECT MOVIE
  ------------------------------------------------ */
  const toggleSelect = (movie) => {
    const updated = selected.find((s) => s.id === movie.id)
      ? selected.filter((s) => s.id !== movie.id)
      : [...selected, movie];

    const remainingGenreIds = new Set(
      updated.flatMap((selectedMovie) => selectedMovie.genre_ids || [])
    );
    const remainingRemovedGenres = removedMovieGenres.filter((genreId) =>
      remainingGenreIds.has(genreId)
    );
    // Manual decreases may offset movie counts. Do not retain a negative
    // total after removing movies, or the next increase could do nothing.
    const nextManualGenreWeights = { ...manualGenreWeights };
    for (const genre of genreList) {
      const movieWeight = remainingRemovedGenres.includes(genre.id)
        ? 0
        : updated.filter((movie) => movie.genre_ids?.includes(genre.id)).length;
      if (nextManualGenreWeights[genre.id] < -movieWeight) {
        nextManualGenreWeights[genre.id] = -movieWeight;
      }
    }
    const nextPreferences = combinedGenrePreferences({
      selectedMovies: updated,
      removedMovieGenres: remainingRemovedGenres,
      manualGenreWeights: nextManualGenreWeights,
      genreList,
    });
    const nextTotalWeight = nextPreferences.reduce(
      (total, genre) => total + genre.weight, 0
    );
    if (nextTotalWeight > MAX_GERS_GENRE_WEIGHT) {
      setRecommendationError(
        "Genre weight limit reached. Decrease a weight or remove a preference before selecting more movies."
      );
      return;
    }
    setSelected(updated);
    setRemovedMovieGenres(remainingRemovedGenres);
    setManualGenreWeights(nextManualGenreWeights);
    invalidateRecommendations();
    bumpRepresentationRevision();
  };

  const removeGenrePreference = (genreId) => {
    setManualGenreWeights((weights) => {
      const updated = { ...weights };
      delete updated[genreId];
      return updated;
    });
    if (selected.some((movie) => movie.genre_ids?.includes(genreId))) {
      setRemovedMovieGenres((removed) =>
        removed.includes(genreId) ? removed : [...removed, genreId]
      );
    }
    invalidateRecommendations();
    bumpRepresentationRevision();
  };

  /* -----------------------------------------------
     SELECT GENRE
  ------------------------------------------------ */
  const increaseGenreWeight = (id) => {
    if (genreWeightLimitReached) return;
    setManualGenreWeights((weights) => ({
      ...weights,
      [id]: (weights[id] || 0) + 1,
    }));
    invalidateRecommendations();
    bumpRepresentationRevision();
  };

  const decreaseGenreWeight = (id) => {
    const preference = genrePreferences.find((genre) => genre.id === id);
    if (!preference) return;
    if (preference.weight === 1) {
      removeGenrePreference(id);
      return;
    }
    setManualGenreWeights((weights) => ({
      ...weights,
      [id]: (weights[id] || 0) - 1,
    }));
    invalidateRecommendations();
    bumpRepresentationRevision();
  };

  const handleGenreArrowKey = (event, id) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      if (event.key === "ArrowUp") increaseGenreWeight(id);
      else decreaseGenreWeight(id);
    }
  };

  /* -----------------------------------------------
     GET RECOMMENDATIONS
  ------------------------------------------------ */
  const handleRecommend = async (anotherBatch = false) => {
    const combinedGenreNames = genreNamesWithFrequencies(genrePreferences);

    if (combinedGenreNames.length === 0) {
      setRecommendationError("Select at least one movie or genre first.");
      return;
    }

    const requestId = ++recommendationRequestId.current;
    session.beginRequest();
    setLoading(true);
    setRecommendationError("");

    try {
      const payload = {
        genres: combinedGenreNames,
        // The frozen hybrid also needs the RecVAE interaction side.
        liked_movie_ids: selected.map((movie) => Number(movie.movieId)),
        // Participant rating semantics are intentionally unresolved for GERS;
        // null is logged and the frozen serving model keeps its fixed 5.0 input.
        preference_evidence: selected.map((movie) => ({
          movie_id: Number(movie.movieId),
          rating: null,
        })),
        // The onboarding catalog is for preference elicitation only. Neither
        // selected nor unselected catalog titles may reappear as results.
        excluded_movie_ids: session.exclusions(anotherBatch),
        ...(anotherBatch ? { exclude_previously_recommended: true } : {}),
        catalog_fingerprint: servingDeployment.matrix_fingerprint,
        onboarding_fingerprint: pilotManifest.fingerprint,
        context,
        alpha: GERS_ALPHA,
        top_k: topK,
        min_release_year: MIN_RECOMMENDATION_YEAR,
      };
      const input = recommendationInputSnapshot("GERS", payload);
      const meta = await study.metadata({
        taskId: "1b",
        input,
        representationRevision: representationRevisionRef.current,
      });
      if (requestId !== recommendationRequestId.current) return;
      payload.study = meta;
      activeRecommendationRequest.current = meta;
      currentRecommendationSignature.current = meta.input_signature;
      const res = await axios.post(`${PILOT_API_URL}/gers`, payload);

      if (
        requestId !== recommendationRequestId.current ||
        !responseMatchesCurrent(
          activeRecommendationRequest.current,
          res.data.study,
          currentRecommendationSignature.current
        )
      ) {
        return;
      }

      const items = filterRecommendationRecords(res.data.items || [], selected,
        session.exclusions(anotherBatch).map(movieId => ({ movieId })));
      const posters = items.map(item => resolveMovieLensRecommendation(item, null));
      session.display(posters, meta);
      session.setNotice(items.length ? "" : anotherBatch ? "No unseen eligible movies remain in this session." : "No eligible recommendations match the current filters.");
      Promise.all(items.map(async item => {
        const metadata = await fetchPoster(item.movie_id, item.title, item.tmdb_id, item.imdb_id);
        return { ...resolveMovieLensRecommendation(item, metadata), rating: metadata?.rating ?? null };
      })).then(withPosters => {
        if (requestId === recommendationRequestId.current && responseMatchesCurrent(
          activeRecommendationRequest.current, meta, currentRecommendationSignature.current)) {
          setRecommendations(withPosters);
        }
      }).catch(() => {});

    } catch (err) {
      if (requestId === recommendationRequestId.current) {
        console.error("GERS ERROR:", err);
        setRecommendationError(
          apiErrorMessage(
            err,
            "Recommendations are temporarily unavailable. Please try again."
          )
        );
      }
    }

    if (requestId === recommendationRequestId.current) setLoading(false);
  };

  usePageRefresh(session.refreshPending, !loadingMovies && genrePreferences.length > 0, () => handleRecommend());

  /* -----------------------------------------------
     UI
  ------------------------------------------------ */
  return (
    <div className="theme-page min-h-screen overflow-x-hidden bg-gradient-to-br from-[#0d0017] via-[#0b0012] to-black text-white px-4 sm:px-6 py-6 sm:py-8">

      {/* LOGO */}
      <Link to="/" aria-label="Go to landing page" className="absolute top-4 left-4 sm:top-6 sm:left-8 z-20">
        <motion.img
          src={publicAsset("logo.png")}
          alt="Studio Auréa"
          className="h-14 sm:h-20 drop-shadow-[0_0_35px_#00ff99aa]"
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
        />
      </Link>

      {/* BACK BUTTON */}
      <button
        onClick={goBack}
        className="absolute top-4 right-36 sm:top-6 sm:right-40 px-4 sm:px-5 py-2 rounded-2xl bg-white/5
          text-sm sm:text-base
          border border-green-500 text-white backdrop-blur-xl
          shadow-[0_0_25px_#00ff99aa] hover:shadow-[0_0_40px_#00ff99ff]"
      >
        ← Back
      </button>

      {/* TITLE */}
      <div className="max-w-[1600px] mx-auto mt-24 sm:mt-28 mb-8 sm:mb-10">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-green-300/80 mb-3">
          Build your taste from movies and genres
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-green-300 drop-shadow-[0_0_25px_#00ff99aa]">
          GERS – Genre-Based Recommender
        </h1>
      </div>

      <div className="max-w-[1600px] mx-auto flex flex-col xl:flex-row gap-8 xl:gap-10">

        {/* LEFT SIDE */}
        <div className="w-full xl:w-3/5">
          <SelectedMovies movies={selected} system="GERS" onRemove={toggleSelect} />
          <h2 className="text-lg font-semibold mb-1">Movie catalog</h2>
          <p className="text-sm text-gray-400 mb-3">Select movies you like:</p>

          {loadingMovies ? (
            <div className="text-center text-gray-400 py-10">
              Loading movies...
            </div>
          ) : catalogMovies.length === 0 ? (
            <p className="py-6 text-sm text-gray-400">All movies in this catalog are selected. Remove a movie above to add it back.</p>
          ) : (

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 relative z-10 overflow-visible">

              {catalogMovies.map((m) => (
                <MoviePosterPreview key={m.movieId} movieId={m.movieId} title={m.title}
                  urls={[m.poster, ...(m.poster_urls || [])]} overview={m.overview}
                  tmdbId={m.tmdbId} imdbId={m.imdbId} year={m.year} rating={m.rating} genres={m.genres}>
                <motion.button
                  key={m.id}
                  data-catalog-movie-id={m.movieId}
                  type="button"
                  aria-pressed={selected.some((s) => s.id === m.id)}
                  whileHover={{ scale: 1.08 }}
                  onClick={() => toggleSelect(m)}
                  className={`relative cursor-pointer rounded-xl p-[3px] text-left transition-all
                    ${
                      selected.find((s) => s.id === m.id)
                        ? "ring-2 ring-green-400 shadow-[0_0_20px_#00ff99aa]"
                        : "ring-1 ring-white/10 hover:ring-green-400 hover:shadow-[0_0_20px_#00ff9955]"
                    }`}
                >
                  <MoviePoster movieId={m.movieId} title={m.title} urls={[m.poster, ...(m.poster_urls || [])]} />

                  <p className="text-sm mt-1 text-center md:line-clamp-3">{m.title}</p>

                </motion.button>
                </MoviePosterPreview>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT SIDE */}
        <div className="theme-preferences w-full xl:w-2/5 xl:sticky xl:top-6 self-start bg-white/5 border border-green-400 rounded-3xl p-4 sm:p-5 backdrop-blur-xl shadow-[0_0_30px_#00ff9933]">

          <div role="group" aria-labelledby="gers-genre-preferences" className="mb-4 rounded-xl border border-green-400/40 bg-black/20 p-3">
            <h2 id="gers-genre-preferences" className="text-sm font-semibold text-green-300 mb-2">
              Your genre preferences
            </h2>
            {genrePreferences.length > 0 && (
              <p className="mb-2 text-xs leading-relaxed text-gray-400">
                Use ↑ and ↓ to adjust weights, then get recommendations. A weight of zero removes the preference.
              </p>
            )}

            {genrePreferences.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {genrePreferences.map((genre) => (
                  <span
                    key={genre.id}
                    className={`flex min-w-0 items-center gap-1 rounded-lg border px-2 py-1 text-xs ${
                      genre.weight > 1
                        ? "order-first border-green-300 bg-green-700/60 text-white shadow-[0_0_12px_#00ff9966]"
                        : "border-white/20 bg-white/10 text-gray-200"
                    }`}
                  >
                    <span className="min-w-0 flex-1 break-words leading-snug">{genre.name}</span>
                    <span className="w-6 shrink-0 text-center font-bold tabular-nums text-green-200">×{genre.weight}</span>
                    <button
                      type="button"
                      onClick={() => increaseGenreWeight(genre.id)}
                      onKeyDown={(event) => handleGenreArrowKey(event, genre.id)}
                      disabled={genreWeightLimitReached}
                      aria-label={`Increase ${genre.name} weight (current weight: ${genre.weight})`}
                      title={`Increase ${genre.name} weight`}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-base hover:bg-white/10 focus:outline-none focus:ring-1 focus:ring-green-300 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <span aria-hidden="true">↑</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => decreaseGenreWeight(genre.id)}
                      onKeyDown={(event) => handleGenreArrowKey(event, genre.id)}
                      aria-label={`Decrease ${genre.name} weight (current weight: ${genre.weight})`}
                      title={`Decrease ${genre.name} weight`}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-base hover:bg-white/10 focus:outline-none focus:ring-1 focus:ring-green-300"
                    >
                      <span aria-hidden="true">↓</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeGenrePreference(genre.id)}
                      aria-label={`Remove ${genre.name} preference`}
                      title={`Remove ${genre.name}`}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md
                        text-sm leading-none text-gray-300 transition-colors
                        hover:bg-red-500/30 hover:text-white focus:outline-none
                        focus:ring-1 focus:ring-red-300"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">
                Select movies or click genres below to add your preferences.
              </p>
            )}
            {genreWeightLimitReached && (
              <p className="mt-2 text-xs text-gray-400">
                Total genre weight limit reached ({MAX_GERS_GENRE_WEIGHT}). Decrease a weight or remove a preference to add more.
              </p>
            )}
          </div>

          {/* GENRE BUTTONS */}
          <h2 className="text-base font-semibold mb-2">Select genres:</h2>

          <div className="flex flex-wrap gap-1.5 mb-4">
            {genreList.map((g) => (
              <button
                key={g.id}
                type="button"
                aria-pressed={genrePreferences.some((genre) => genre.id === g.id)}
                onClick={() => increaseGenreWeight(g.id)}
                disabled={genreWeightLimitReached}
                title="Click to add or increase weight"
                className={`min-h-8 px-2.5 py-1 rounded-md text-xs border transition-all ${
                  genrePreferences.some((genre) => genre.id === g.id)
                    ? "bg-green-700 border-green-400 shadow-[0_0_15px_#00ff99aa]"
                    : "bg-white/10 border-white/20 hover:bg-white/20"
                }`}
              >
                {g.name}
              </button>
            ))}
          </div>

          <div className="mb-6">
            <label htmlFor="gers-top-k" className="text-sm font-semibold">
              Number of recommendations: {topK}
            </label>
            <input
              id="gers-top-k"
              type="range"
              min="1"
              max="25"
              value={topK}
              onChange={(event) => {
                setTopK(Number(event.target.value));
                invalidateRecommendations();
              }}
              className="w-full mt-2 accent-green-400"
            />
          </div>

          {/* BUTTON */}
          <button
            onClick={() => handleRecommend()}
            disabled={loading}
            className="w-full py-3 rounded-xl font-semibold text-lg
              bg-[#121212] border border-green-500 shadow-[0_0_25px_#00ff99aa]
              hover:shadow-[0_0_40px_#00ff99dd]
              disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            {loading ? "Finding your movies…" : "Get Recommendations"}
          </button>

          {session.notice && <p role="status" className="mt-3 text-sm text-gray-300">{session.notice}</p>}

          {recommendationError && (
            <p role="alert" className="mt-3 text-sm text-red-300">
              {recommendationError}
            </p>
          )}

          {/* RESULTS */}
          <RecommendationGrid system="GERS" movies={recommendations}
            previousMovies={session.previousRecommendations} />
        </div>
      </div>
    </div>
  );
}
